import { HttpClient } from '@angular/common/http';
import {
  Inject,
  Injectable,
  OnDestroy
} from '@angular/core';
import { ChartsDashboardStore } from 'app/charts-dashboard/state/dashboard/charts-dashboard.store';
import { Observable } from 'rxjs';
import {
  CardTypeEnum,
  ChartType,
  ChartTypeEnum,
  CustomControlsNameEnum,
  Datetime,
  DateType,
  ICardChartData,
  IChartFilter,
  IChartFilterPattern,
  ICompareDialogOutput,
  ILayout,
  ILayoutEntity,
  IOptionInfoStrikes,
  ISelectedFilters,
  LayoutNameEnum,
  RealTimeEnum,
  Strike,
  SystemControlsNameEnum
} from 'app/charts-dashboard/interfaces';
import {
  ChartsDashboardSeriesQuery,
  ChartsDashboardSeriesStore,
  IChartSeries,
  SeriesTypeEnum,
  SeriesUpdateOptionsType
} from './series';
import {
  guid,
  ID
} from '@datorama/akita';
import {
  ChartsCardUpdateOptionsType,
  ChartsDashboardCardQuery,
  ChartsDashboardCardStore,
  ICard
} from 'app/charts-dashboard/state/card';
import {
  ChartsDashboardQuery,
  IChartDashboard
} from './dashboard';
import { Destroyer } from 'app/common/utils';
import {
  addSingleContractAnalyzerExpirations,
  addSingleContractAnalyzerStrikes,
  addNewCard,
  initialCard,
  initialSeriesSPYSpotPrice,
  initialSeriesSPYImpliedVolatility,
  initialSeriesSPYHistoricalVolatilityHV10,
  initialSeriesSPYHistoricalVolatilityHV180,
  initialSeriesSPYTotalOIVolumeCall,
  initialSeriesSPYTotalOIVolumePut,
  voidCard,
  totalOiChartType
} from './data-state.mock';
import * as TabsActions from '@iv/store/tabs/tabs.actions';
import { DeleteTabAction } from '@iv/store/tabs/tabs.actions';
import { AppStore } from '@iv/store/app.store';
import * as Redux from 'redux';
import { AppState } from '@iv/store/app.reducer';
import { Tab } from '@iv/model/tabs.model';
import { getCurrentTab } from '@iv/store/tabs/tabs.selectors';
import { ChartsHttpService } from '../services/charts-http.service';
import { ChartsCommonService } from '../services/charts-common.service';
import {
  distinctUntilChanged,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { ChartDashboardUpdateOptionsType } from 'app/charts-dashboard/state/dashboard/dashboard.model';
import { Instrument } from '@iv/model/instrument';
import { isIntraday } from '../helpers/charts-module-helpers';
import { CommonService } from 'app/core/services/common.service';

@Injectable()
export class ChartsDashboardStateService extends Destroyer implements OnDestroy {

  constructor(
    private readonly dashboardStore: ChartsDashboardStore,
    private readonly dashboardQuery: ChartsDashboardQuery,
    private readonly seriesStore: ChartsDashboardSeriesStore,
    private readonly seriesQuery: ChartsDashboardSeriesQuery,
    private readonly cardStore: ChartsDashboardCardStore,
    private readonly cardQuery: ChartsDashboardCardQuery,
    private readonly chartsHttpService: ChartsHttpService,
    private readonly chartsCommonService: ChartsCommonService,
    private readonly http: HttpClient,
    private readonly commonService: CommonService,
    @Inject(AppStore) private readonly store: Redux.Store<AppState>
  ) {
    super();
    // TODO: После перевода Tabs на Akita, выпилить
    this.unsubscribeStore = store.subscribe(() => {
      const lastAction: DeleteTabAction = store.getState().lastAction as any;

      if (lastAction.type === '[Tabs] Delete tab' && lastAction.tab.data.dashboard) {
        this.deleteDashboard(lastAction.tab.data.dashboard);
      }
    });
  }

  public layouts: ILayout[];
  public layouts$: Observable<ILayout[]>;
  public allChartTypes$: Observable<ChartType[]>;
  public allParameters: IChartFilter[] = [];

  private readonly unsubscribeStore;
  private defaultColors = ['#c96363', '#066907', '#130669', '#690606', '#ed4545', '#45ed55', '#000000', '#a58e0e', '#6d632e', '#2e636d', '#652e6d', '#eb6814'];
  private availableColors: { [cardId: string]: Array<string> } = {};
  public ngOnDestroy() {
    this.unsubscribeStore();

    super.ngOnDestroy();
  }

  // TODO: После перевода всей сторы приложения на Akita - выпилить \ переделать на обращение к сущности Tab через новый стейт менеджер
  public updateTabData(dashboard: IChartDashboard<ICard<IChartSeries>>): void {
    this.store.dispatch(TabsActions.updateTabData({
      name: 'dashboard',
      value: dashboard
    }));
  }

  // TODO: скорее всего преобразуется после подключения нового апи и удалению redux сторы из чартов и проекта
  public loadDashboard(dashboard: IChartDashboard<ICard<IChartSeries>>): void {
    if (dashboard.cards[0].hasOwnProperty('chartLayout')) {
      dashboard = this.convertOldTab(dashboard);
    }

    const series: IChartSeries[] = dashboard.cards.map((card: ICard<IChartSeries>) => {
      return card.chartSeries;
    }).flat();
    const cards: ICard<ID>[] = dashboard.cards.map((card: ICard<IChartSeries>) => {
      return {
        ...card,
        chartSeries: card.chartSeries.map((_series: IChartSeries) => _series.id)
      };
    });

    this.createSeries(series);
    this.createCard(cards);
    this.createDashboard({
      ...dashboard,
      cards: dashboard.cards.map((card: ICard<IChartSeries>) => card.id)
    });
    this.automaticallyUpdateSaveTabData(dashboard.id);
  }

  public initDashboard(layouts: ILayout[]): void {
    this.layouts = layouts;
    const currentTab: Tab = getCurrentTab(this.store.getState());

    if (currentTab.data.dashboard) {
      this.loadDashboard(currentTab.data.dashboard);
    } else {
      const card: ICard<ID> = initialCard(ChartTypeEnum.SPOT_CHART);
      const series: IChartSeries = initialSeriesSPYSpotPrice();
      card.filters = [...this.chartsCommonService.getParametersChart(card.chartId, this.allParameters)];
      card.chartSeries = [series.id];

      const card2: ICard<ID> = initialCard(ChartTypeEnum.IMPLIED_VOLATILITY);
      const series2: IChartSeries = initialSeriesSPYImpliedVolatility();
      card2.filters = [...this.chartsCommonService.getParametersChart(card2.chartId, this.allParameters)];
      card2.chartSeries = [series2.id];

      const card3: ICard<ID> = initialCard(ChartTypeEnum.HISTORICAL_VOLATILITY);
      const series3: IChartSeries = initialSeriesSPYHistoricalVolatilityHV10();
      const series4: IChartSeries = initialSeriesSPYHistoricalVolatilityHV180();
      card3.filters = [...this.chartsCommonService.getParametersChart(card3.chartId, this.allParameters)];
      card3.chartSeries = [series4.id, series3.id];

      const card4: ICard<ID> = initialCard(ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME);
      const series5: IChartSeries = initialSeriesSPYTotalOIVolumeCall();
      const series6: IChartSeries = initialSeriesSPYTotalOIVolumePut();
      card4.filters = [...this.chartsCommonService.getParametersChart(card4.chartId, [...this.allParameters, ...totalOiChartType])];
      card4.chartSeries = [series5.id, series6.id];

      const dashboard: IChartDashboard<ID> = {
        id: guid(),
        layout: this.getLayout(LayoutNameEnum.LAYOUT_FOUR_CARD_SQUARE),
        cards: [card.id, card2.id, card3.id, card4.id]
      };

      this.automaticallyUpdateSaveTabData(dashboard.id);
      this.createSeries([series, series2, series3, series4, series5, series6]);
      this.createCard([card, card2, card3, card4]);

      this.createDashboard(dashboard);
    }
  }

  public createDashboard(dashboard: IChartDashboard<ID>): void {
    this.dashboardStore.add(dashboard);
  }

  public updateDashboard(
    dashboardId: ID = this.dashboardQuery.getCurrentDashboardId,
    value?: ChartDashboardUpdateOptionsType<ID>
  ): void {
    this.dashboardStore.update(dashboardId, {
      ...this.dashboardQuery.getEntity(dashboardId),
      ...value
    });
  }

  public deleteDashboard(dashboard: IChartDashboard<ICard<ID>>): void {
    const cardIds: ID[] = this.dashboardQuery.getEntity(dashboard.id).cards;
    const seriesIds: ID[] = cardIds.map((id: ID) => {
      return this.seriesQuery.getSeriesFromCard(this.cardQuery.getEntity(id)).map((series: IChartSeries) => {
        return series.id;
      });
    }).flat();

    this.deleteCard(cardIds);
    this.deleteSeries(seriesIds);
    this.dashboardStore.remove(dashboard.id);
  }

  public selectChartLayout(layoutName: LayoutNameEnum): void {
    const dashboard: IChartDashboard<ID> = this.dashboardQuery.getCurrentDashboard;
    const layout: ILayoutEntity = this.getLayout(layoutName);

    this.updateDashboard(dashboard.id, {
      layout,
      cards: [...this.changeListCards([...dashboard.cards], layout.cardsAmount)]
    });
  }

  public createCard(card: (ICard<ID> | ICard<ID>[])): void {
    this.cardStore.add(card);
  }

  public fillCard(cardId: ID, chartData: ICardChartData, dashboardId: ID): void {
    this.availableColors[cardId] = [...this.defaultColors]
    const seriesId: ID = guid();
    const seriesData: IChartSeries = {
      id: seriesId,
      stock: chartData.stock,
      selectedFilters: chartData.selectedFilters,
      settings: {
        color: this.availableColors[cardId][Math.floor(Math.random() * this.availableColors[cardId].length)]
      },
      type: SeriesTypeEnum.MAIN
    };

    if (chartData.chart === ChartTypeEnum.IV_DIFFERENCE) {
      (seriesData as IChartSeries<undefined>).stock = undefined;
    }

    this.createSeries(seriesData);

    this.cardStore.update(cardId, {
      type: CardTypeEnum.FILLED,
      chartId: chartData.chart,
      filters: chartData.filters,
      chartSeries: [seriesId]
    });

    this.updateDashboard(dashboardId);
  }

  public updateCard(cardId: ID, value: ChartsCardUpdateOptionsType<ID>): void {
    this.cardStore.update(cardId, {
      ...this.cardQuery.getEntity(cardId),
      ...value
    });
  }

  public deleteCard(cardId: ID | ID[]): void {
    this.cardStore.remove(cardId);
  }

  public toggleFullScreen(cardId: ID, fullscreen: boolean): void {
    this.updateCard(cardId, {fullscreen});
  }

  public deleteChartArea(cardId: ID): void {
    const dashboardSnapshot: IChartDashboard<ICard<IChartSeries>> = this.dashboardQuery.getDashboardSnapshot(this.dashboardQuery.getCurrentDashboardId);
    const oldAddCard: ICard<IChartSeries> = dashboardSnapshot.cards.find((card: ICard<IChartSeries>) => card.type === CardTypeEnum.ADD_NEW_CARD);
    const oldCard: ICard<ID> = this.cardQuery.getEntity(cardId);
    const oldCardIndex: number = dashboardSnapshot.cards.findIndex((card: ICard<IChartSeries>) => card.id === oldCard.id);
    let newCard: ICard<ID> = {
      ...oldCard,
      type: CardTypeEnum.ADD_NEW_CARD,
      chartSeries: [],
      filters: [],
      chartId: null,
      fullscreen: false
    };
    let cards: ID[] = [...this.dashboardQuery.getCurrentDashboard.cards];

    if (oldAddCard) {
      newCard = {...newCard, type: CardTypeEnum.VOID};
      cards.splice(oldCardIndex, 1);
      cards = [...cards, newCard.id];
    } else {
      cards.splice(oldCardIndex, 1);
      cards = [...cards, newCard.id];
    }

    this.deleteSeries(oldCard.chartSeries);
    this.updateCard(cardId, newCard);
    this.updateDashboard(dashboardSnapshot.id, {cards: cards});
  }

  public createSeries(series: (IChartSeries | IChartSeries[])): void {
    this.seriesStore.add(series);
  }

  public updateSeries(seriesId: ID, value: SeriesUpdateOptionsType): void {
    this.seriesStore.update(seriesId, {
      ...this.seriesQuery.getEntity(seriesId),
      ...value
    });
  }

  public deleteSeries(seriesId: ID | ID[]): void {
    this.seriesStore.remove(seriesId);
  }

    public compareSeries(chartData:ICompareDialogOutput): void {
    const parentCardId = chartData.parentCardId;
    let stock = chartData.stock;
    const selectedFilters = chartData.selectedFilters;
    const seriesId: ID = guid();
    const mainSeries: IChartSeries = this.cardQuery.getEntity(parentCardId)
      .chartSeries
      .map((id: ID) => this.seriesQuery.getEntity(id))
      .find((series: IChartSeries) => series.type === SeriesTypeEnum.MAIN);
    const isMaturity: boolean = this.chartsCommonService.isMaturity(this.cardQuery.getEntity(parentCardId).chartId);

    if (!stock) { // Если нет стока - то записываем сток из main series
      stock = this.seriesQuery.getEntity(this.cardQuery.getEntity(parentCardId).chartSeries[0]).stock;
    }

    this.updateCard(parentCardId, {
      chartSeries: [...this.cardQuery.getEntity(parentCardId).chartSeries, seriesId]
    });

    this.createSeries({
      id: seriesId,
      stock,
      selectedFilters: {
        ...selectedFilters,
        realtime: isMaturity
          ? {name: RealTimeEnum.OFF, description: 'Off'}
          : mainSeries.selectedFilters.realtime
      },
      settings: {
        color: this.availableColors[parentCardId][Math.floor(Math.random() * this.availableColors[parentCardId].length)]
      },
      filters:chartData.filters,
      type: SeriesTypeEnum.COMPARE
    });

    this.updateDashboard();
  }

  public changeStockInSeries(chartData: ICompareDialogOutput): void {
    const stock = chartData.stock;
    this.updateSeries(chartData.seriesId, {stock});
    this.updateDashboard();
  }

  public changeIVDiffStock(seriesId: ID, filters: ISelectedFilters): void {
    this.updateSeries(seriesId, {
      selectedFilters: {
        ...this.seriesQuery.getEntity(seriesId).selectedFilters,
        ...filters,
      }
    });
    this.updateDashboard();
  }

  public changeStockInIntraDayChart(chartData): void {
      this.updateSeries(
          chartData.seriesId,
          {...chartData}
        );
      this.updateDashboard();
  }

  public changeStockInAllSeriesCard(card: ICard<ID>, stock: Instrument): void {
    card.chartSeries.forEach((id: ID) => this.updateSeries(id, {stock}));
    this.updateDashboard();
  }

  public updateMaturityTimestamp(seriesId: ID, datetime: Datetime): void {
    this.updateSeries(seriesId, {
      selectedFilters: {
        ...this.seriesQuery.getEntity(seriesId).selectedFilters,
        datetime
      }
    });
  }

  public updateSelectedFilters(card: ICard<ID>, seriesId: ID, filters: ISelectedFilters): void {
    const selectedFilters: ISelectedFilters = this.seriesQuery.getEntity(seriesId).selectedFilters;

    if (
      ((filters.timeRange && filters.timeRange.name !== selectedFilters.timeRange.name) ||
        (filters.eodTimeRange && filters.eodTimeRange.name !== selectedFilters.eodTimeRange.name) ||
        (filters.realtime && filters.realtime.name !== selectedFilters?.realtime?.name)) &&
      this.chartsCommonService.isHistorical(card.chartId)
    ) {
      this.updateFiltersInAllSeries(card, {
        timeRange: filters.timeRange,
        eodTimeRange: filters.eodTimeRange,
        realtime: filters.realtime,
        vModeToggler: filters.vModeToggler
      });
      return;
    }

    this.updateSeries(seriesId, {
      selectedFilters: {
        ...selectedFilters,
        ...filters
      }
    });
    this.updateDashboard();
  }

  public updateFiltersInAllSeries(card: ICard<ID>, filters: ISelectedFilters): void {
    card.chartSeries.forEach((id: ID) => {
      this.updateSeries(id, {
        selectedFilters: {
          ...this.seriesQuery.getEntity(id).selectedFilters,
          ...filters
        }
      });
    });

    this.updateDashboard();
  }

  private automaticallyUpdateSaveTabData(dashboardId: ID) {
    this.dashboardQuery.selectDashboard(dashboardId)
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((dashboard: IChartDashboard<ICard<IChartSeries>>) => this.updateTabData(dashboard));
  }

  private getLayout(layoutName: LayoutNameEnum): ILayoutEntity {
    return this.layouts
      .map((layout: ILayout) => layout.layoutEntity
        .find((layoutEntity: ILayoutEntity) => layoutEntity.name === layoutName))
      .filter(x => x !== undefined)[0];
  }

  private changeListCards(cardIds: ID[], cardsAmount: number): ID[] {
    const cards: ICard<ID>[] = cardIds.map((cardId: ID) => this.cardQuery.getEntity(cardId));
    const markedIds: ID[] = [];

    for (let i = 0; i < cardsAmount; i++) {
      if (cardIds.length < cardsAmount) {
        if (cardsAmount === 1) {
          cards.push(addNewCard());
          cardIds.push(cards[0].id);
        }

        if (!cardIds[i]) {
          const card: ICard<ID> = cards.find(_card => _card.id === cardIds[i - 1]);

          if (card.type === CardTypeEnum.ADD_NEW_CARD || card.type === CardTypeEnum.VOID) {
            cards.push(voidCard());
            cardIds.push(cards[cards.length - 1].id);
            continue;
          }

          cards.push(addNewCard());
          cardIds.push(cards[cards.length - 1].id);
        }
      }

      if (cardIds.length > cardsAmount) {
        const needDeleted: number = cardIds.length - cardsAmount;
        const newCardIds = [...cardIds];

        newCardIds.reverse().forEach((cardId: ID, index: number) => {
          if (index < needDeleted) {
            markedIds.push(cardId);
          }
        });
        cardIds.length = cardsAmount;

        const seriesIds: ID[] = markedIds.map((id: ID) => {
          return this.seriesQuery.getSeriesFromCard(this.cardQuery.getEntity(id)).map((series: IChartSeries) => {
            return series.id;
          });
        }).flat();

        this.deleteCard(markedIds);
        this.deleteSeries(seriesIds);
      }
    }

    if (!markedIds.length) {
      this.createCard(cards);
    }

    return cardIds;
  }

  // Типов от старых табов не осталось, поэтому желательно менять код этой функции крайне аккуратно (лучше не менять)
  // Дополнять стоит только в том случае, если в стейт добавляются новые какие-то поля у объектов
  private convertOldTab(oldDashboard): IChartDashboard<ICard<IChartSeries>> {
    const convertChartId = (oldChartId: string): ChartType => {
      switch (oldChartId) {
        case 'termStructureRolling':
        case 'impliedVolatility':
        case 'historicalVolatility':
        case 'ivxHistory':
        case 'spotChart':
        case 'skewChart':
        case 'ivPremiumChart':
          return oldChartId.replace(/\.?([A-Z])/g, function (x, y) {
            return '_' + y.toLowerCase();
          }).replace(/^_/, '').toUpperCase() as ChartType;
        case 'totalOpenInterestVolume':
          return 'TOTAL_OI_VOLUME' as ChartType;
      }
    };

    return {
      ...oldDashboard,
      id: guid(),
      cards: oldDashboard.cards.map(oldCard => {
        return {
          chartId: oldCard.type === CardTypeEnum.FILLED ? convertChartId(oldCard.chartLayout.chart.chartId) : null,
          filters: oldCard.type === CardTypeEnum.FILLED
            ? [
              ...this.chartsCommonService.getParametersChart(convertChartId(oldCard.chartLayout.chart.chartId), this.allParameters),
              oldCard.chartLayout.chart.chartId === 'spotChart' ? {
                parameterId: CustomControlsNameEnum.V_MODE_TOGGLER,
                name: CustomControlsNameEnum.V_MODE_TOGGLER,
                type: null,
                pattern: [{description: false, name: false}, {description: true, name: true}],
                description: CustomControlsNameEnum.V_MODE_TOGGLER
              } : null
            ].filter(el => el !== null)
            : [],
          chartSeries: oldCard.type === CardTypeEnum.FILLED ? [
            {...oldCard.chartLayout.mainSeries, type: SeriesTypeEnum.MAIN},
            ...oldCard.chartLayout.compareSeries.map(oldSeries => {
              return {...oldSeries, type: SeriesTypeEnum.COMPARE};
            })
          ].map(newSeries => {
            if (
              oldCard.chartLayout.chart.chartId === 'historicalVolatility' ||
              oldCard.chartLayout.chart.chartId === 'totalOpenInterestVolume'
            ) {
              if (oldCard.chartLayout.chart.chartId === 'totalOpenInterestVolume') {
                newSeries.selectedFilters.optionType = {
                  ...newSeries.selectedFilters.optionType,
                  name: newSeries.selectedFilters.optionType.name === 'Call&Put' ? 'CALL_PUT' : newSeries.selectedFilters.optionType.name.toUpperCase()
                };
              }
              newSeries.selectedFilters.eodTimeRange = newSeries.selectedFilters.timeRange;
              delete newSeries.selectedFilters.timeRange;
            }

            if (
              oldCard.chartLayout.chart.chartId === 'historicalVolatility' ||
              oldCard.chartLayout.chart.chartId === 'ivPremiumChart'
            ) {
              newSeries.selectedFilters.hvTerm = {
                ...newSeries.selectedFilters.hvTerm,
                name: `T${newSeries.selectedFilters.hvTerm.name}`
              };
            }

            if (
              oldCard.chartLayout.chart.chartId === 'ivxHistory' ||
              oldCard.chartLayout.chart.chartId === 'impliedVolatility' ||
              oldCard.chartLayout.chart.chartId === 'ivPremiumChart' ||
              oldCard.chartLayout.chart.chartId === 'skewChart'
            ) {
              newSeries.selectedFilters.ivxTerm = {
                ...newSeries.selectedFilters.ivxTerm,
                name: `T${newSeries.selectedFilters.ivxTerm.name}`
              };
            }

            if (oldCard.chartLayout.chart.chartId === 'spotChart') {
              newSeries.selectedFilters = {
                ...newSeries.selectedFilters,
                vModeToggler: false
              };
            }

            if (
              oldCard.chartLayout.chart.chartId === 'spotChart' ||
              oldCard.chartLayout.chart.chartId === 'ivxHistory' ||
              oldCard.chartLayout.chart.chartId === 'impliedVolatility' ||
              oldCard.chartLayout.chart.chartId === 'ivPremiumChart'
            ) {
              if (isIntraday(newSeries.selectedFilters.timeRange.name)) {
                const rtValue: RealTimeEnum = isIntraday(newSeries.selectedFilters.timeRange.name) ? RealTimeEnum.OFF : RealTimeEnum.ON;

                newSeries.selectedFilters.realtime = {
                  name: rtValue,
                  description: rtValue,
                };
              }
            }

            if (
              oldCard.chartLayout.chart.chartId === 'skewChart' ||
              oldCard.chartLayout.chart.chartId === 'termStructureRolling'
            ) {
              newSeries.selectedFilters.realtime = {
                ...newSeries.selectedFilters.realtime,
                name: newSeries.selectedFilters.realtime.name.toUpperCase()
              };
            }

            return newSeries;
          }) : [],
          fullscreen: false,
          id: guid(),
          type: oldCard.type
        };
      })
    };
  }

  public removeColorFromAvailable(color: string, cardId: ID): void {
    if (this.availableColors[cardId]) {
      this.availableColors[cardId].splice(this.availableColors[cardId].indexOf(color), 1)
    } else {
      this.availableColors[cardId] = [...this.defaultColors]
      this.availableColors[cardId].splice(this.availableColors[cardId].indexOf(color), 1)
    }
  }

  public restoreColorToDefaults(color: string, cardId: ID): void {
    this.availableColors[cardId].push(color);
  }
}
